import { localized, msg } from "@lit/localize";
import { logError } from "@/lib/log";
import { html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  currentPath,
  matchRoute,
  navigate,
  replaceRoute,
} from "@/routes/router";
import { canManageOrg } from "@/lib/permissions";
import { loadRmWorkspace, resetRmWorkspace } from "@/features/releases/store";
import { auth, fetchMe } from "@/store/auth";
import {
  desktopNotificationsEnabled,
  loadPreferences,
} from "@/store/preferences";
import { detectLocale, setLocale } from "@/i18n";
import { initPush } from "@/store/push";
import { initTheme, loadThemeFromPreferences } from "@/store/theme";
import { loadMotionFromPreferences } from "@/store/motion";
import { fetchProjects } from "@/store/projects";
import { fetchApps } from "@/store/apps";
import { fetchApprovalSummary } from "@/store/approvals";
import {
  fetchUnreadCount,
  handleNotificationEvent,
} from "@/features/notifications/store";
import { fetchWorkspaces } from "@/store/workspaces";
import { connectWs, disconnectWs, wsClient } from "@/store/ws";
import type { DtoNotificationResponse } from "@/api";
import { SignalController } from "@/lib/signal-controller";
import { ensureToastHost } from "./components/ui/toast-host-mount";
import { showToast } from "./components/ui/toast-store";
import { installShortcuts } from "./lib/shortcuts";
import "./components/ui/spinner.ts";
import "./components/search/command-palette.ts";
import "./components/ui/shortcuts-dialog.ts";
import "./features/auth/login-page.ts";
import "./features/auth/magic-link-callback-page.ts";

/**
 * Light DOM: required for @atlaskit/pragmatic-drag-and-drop.
 *
 * If this component had a shadow root, every page component (and all their
 * descendants, including the kanban board) would be inside the shadow tree.
 * Native drag events would have `event.target` retargeted to this shadow host,
 * breaking the DnD library's `draggableRegistry.get(event.target)` lookup.
 *
 * Styles are injected via a `<style>` tag (global in light DOM). Class names
 * are prefixed with `app-` to avoid collisions.
 */

const APP_STYLES = `
iris-app {
  display: block;
  min-height: 100svh;
}
.app-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100svh;
}
/* skip-to-content: hidden until focused */
.skip-link {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  white-space: nowrap;
  border: 0;
}
.skip-link:focus {
  position: fixed;
  top: var(--space-1);
  left: var(--space-1);
  width: auto;
  height: auto;
  padding: var(--space-2) var(--space-4);
  margin: 0;
  overflow: visible;
  clip: auto;
  white-space: normal;
  background: var(--background);
  color: var(--foreground);
  border: 2px solid var(--ring);
  border-radius: var(--radius-md);
  z-index: var(--z-toast);
  font-size: var(--text-sm);
  font-weight: 500;
  text-decoration: none;
  outline: none;
}
`;

const lazyPages = {
  sprintDashboard: () =>
    import("./features/sprint-dashboard/sprint-dashboard-page.ts"),
  apps: () => import("./features/apps/apps-page.ts"),
  appDetail: () => import("./features/apps/app-detail-page.ts"),
  releaseDetail: () => import("./features/releases/release-detail-page.ts"),
  deploymentDetail: () =>
    import("./features/releases/deployment-detail-page.ts"),
  releaseManagement: () =>
    import("./features/releases/release-management-page.ts"),
  projects: () => import("./features/projects/projects-page.ts"),
  projectDetail: () => import("./features/projects/project-detail-page.ts"),
  inbox: () => import("./features/notifications/inbox-page.ts"),
  members: () => import("./features/members/members-page.ts"),
  myTasks: () => import("./features/my-tasks/my-tasks-page.ts"),
  settings: () => import("./features/settings/user-settings-page.ts"),
  workspaceSettings: () =>
    import("./features/settings/workspace-settings-page.ts"),
  notFound: () => import("./features/not-found-page.ts"),
} as const;

@customElement("iris-app")
@localized()
export class IrisApp extends LitElement {
  /** Light DOM: required for @atlaskit/pragmatic-drag-and-drop. */
  createRenderRoot() {
    return this;
  }

  @state()
  private _ready = new Set<string>();

  private async _ensure(
    tag: string,
    loader: () => Promise<unknown>,
  ): Promise<void> {
    if (this._ready.has(tag)) return;
    try {
      await Promise.all([loader(), customElements.whenDefined(tag)]);
      this._ready = new Set(this._ready).add(tag);
    } catch (err) {
      logError(`Failed to load page chunk for ${tag}:`, err);
      showToast(msg("Failed to load page — please refresh"), {
        variant: "error",
      });
      // Retry once after a short delay to recover from transient failures.
      setTimeout(() => {
        loader()
          .then(() => customElements.whenDefined(tag))
          .then(() => {
            this._ready = new Set(this._ready).add(tag);
          })
          .catch(() => {
            // Give up: user must refresh manually.
          });
      }, 1500);
    }
  }

  #signals = new SignalController(this);
  #sidebarLoaded = false;
  #releaseOrgId: string | null = null;
  #wsConnected = false;
  #themeLoaded = false;
  #wsPrev: WebSocket | null = null;
  #wsMessageHandler: ((e: MessageEvent) => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.#signals.watch(currentPath, auth, wsClient);
    initTheme();
    fetchMe();
    ensureToastHost();
    installShortcuts();
    // Pick a locale for unauthenticated routes (login/setup) before prefs
    // load. For authenticated users, loadPreferences() overrides this with
    // the saved language once prefs arrive.
    void setLocale(detectLocale());
  }

  protected willUpdate(): void {
    const path = currentPath.value;
    // Load sidebar data (projects, unread count) once the
    // user is authenticated. app-shell is persistent across navigation, so
    // this runs once per session.
    if (
      !this.#sidebarLoaded &&
      !auth.value.isLoading &&
      auth.value.isAuthenticated
    ) {
      this.#sidebarLoaded = true;
      fetchProjects();
      fetchUnreadCount();
      fetchWorkspaces();
    }
    // Org-scoped app and release data (approvals badge; for owners/admins
    // the "environment has no approver" warning) reloads whenever the
    // active workspace changes, including the first sign-in.
    const orgId = auth.value.user?.active_org_id ?? "";
    if (
      !auth.value.isLoading && auth.value.isAuthenticated &&
      orgId !== this.#releaseOrgId
    ) {
      this.#releaseOrgId = orgId;
      resetRmWorkspace();
      fetchApprovalSummary();
      fetchApps().then(() => {
        if (canManageOrg(auth.value.user?.role)) void loadRmWorkspace();
      });
    }
    // Load theme from server preferences once authenticated
    if (
      !this.#themeLoaded &&
      !auth.value.isLoading &&
      auth.value.isAuthenticated
    ) {
      this.#themeLoaded = true;
      loadThemeFromPreferences();
      loadMotionFromPreferences();
      loadPreferences();
    }
    // Connect WebSocket once authenticated (stays connected across navigation)
    if (
      !this.#wsConnected &&
      !auth.value.isLoading &&
      auth.value.isAuthenticated
    ) {
      this.#wsConnected = true;
      connectWs();
      // Register the service worker + push subscription (best-effort; no-op
      // if VAPID isn't configured or desktop notifications are off).
      initPush();
    } else if (
      this.#wsConnected &&
      !auth.value.isAuthenticated
    ) {
      this.#wsConnected = false;
      disconnectWs();
    }

    // Attach the global notification listener whenever the WebSocket
    // (re)connects, so the bell + inbox update live for mentions,
    // assignments, and comments.
    const ws = wsClient.value;
    if (ws && ws !== this.#wsPrev) {
      this.#attachWs(ws);
    } else if (!ws && this.#wsPrev) {
      this.#detachWs();
    }
    if (auth.value.isLoading || !auth.value.isAuthenticated) return;

    // Redirect old settings routes to new workspace-settings page.
    if (path === "/settings/organization") {
      navigate("/settings/workspace");
      return;
    }
    if (path === "/settings/labels") {
      navigate("/settings/workspace/labels");
      return;
    }
    if (path === "/settings/audit-log") {
      navigate("/settings/workspace/audit");
      return;
    }
    if (path === "/release-management") {
      replaceRoute(`/release-management/releases${window.location.search}`);
      return;
    }
    if (path === "/sprint-dashboard") {
      replaceRoute(`/sprint-dashboard/overview${window.location.search}`);
      return;
    }
    // Keep legacy landing URLs as compatibility aliases while making the
    // Sprint Dashboard the single authenticated landing page.
    if (path === "/" || path === "/login" || path === "/auth/callback") {
      replaceRoute(`/sprint-dashboard/overview${window.location.search}`);
      return;
    }

    if (path === "/apps") {
      this._ensure("iris-apps-page", lazyPages.apps);
    } else if (matchRoute("/apps/:slug", path)) {
      this._ensure("iris-app-detail-page", lazyPages.appDetail);
    } else if (matchRoute("/apps/:slug/releases/:releaseId", path)) {
      this._ensure("iris-release-detail-page", lazyPages.releaseDetail);
    } else if (matchRoute("/apps/:slug/deployments/:deploymentId", path)) {
      this._ensure("iris-deployment-detail-page", lazyPages.deploymentDetail);
    } else if (matchRoute("/release-management/:tab", path)) {
      this._ensure(
        "iris-release-management-page",
        lazyPages.releaseManagement,
      );
    } else if (matchRoute("/sprint-dashboard/:tab", path)) {
      this._ensure("iris-sprint-dashboard-page", lazyPages.sprintDashboard);
    } else if (path === "/projects") {
      this._ensure("iris-projects-page", lazyPages.projects);
    } else if (matchRoute("/projects/:slug", path)) {
      this._ensure("iris-project-detail-page", lazyPages.projectDetail);
    } else if (path === "/inbox") {
      this._ensure("iris-inbox-page", lazyPages.inbox);
    } else if (path === "/members" || matchRoute("/members/:tab", path)) {
      this._ensure("iris-members-page", lazyPages.members);
    } else if (path === "/my-tasks") {
      this._ensure("iris-my-tasks-page", lazyPages.myTasks);
    } else if (path === "/preferences") {
      this._ensure("iris-user-settings-page", lazyPages.settings);
    } else if (
      path === "/settings/workspace" ||
      matchRoute("/settings/workspace/:tab", path)
    ) {
      this._ensure(
        "iris-workspace-settings-page",
        lazyPages.workspaceSettings,
      );
    } else {
      this._ensure("iris-not-found-page", lazyPages.notFound);
    }
  }

  /**
   * renderPage wraps page content with the shared APP_STYLES <style> and the
   * standard loader fallback used while a lazy page chunk is loading.
   * `tag` is the custom element name that must be defined before we render the
   * real page; `content` is the page markup (incl. <iris-command-palette>
   * when appropriate). Collapses the duplicated <style>${APP_STYLES}</style>
   * + loader scaffold that was repeated per-route.
   *
   * `lazy` controls whether the loader is shown until the chunk is ready.
   * Eagerly-imported pages (login, setup, invite-accept, and the initial
   * loader itself) pass `lazy = false` so their content renders immediately;
   * those tags are never added to `_ready` (only `_ensure` does that for lazy
   * routes), so the `_ready.has(tag)` gate would otherwise spin forever.
   */
  private renderPage(
    tag: string,
    content: ReturnType<typeof html>,
    lazy = true,
  ): ReturnType<typeof html> {
    const body = (!lazy || this._ready.has(tag)) ? content : html`
      <div class="app-loader">
        <iris-spinner></iris-spinner>
      </div>
    `;
    return html`
      <style>${APP_STYLES}</style><a href="#main-content"
        class="skip-link">Skip to content</a><main
        id="main-content">${body}</main>
    `;
  }

  private pageWithPalette(tag: string, page: ReturnType<typeof html>) {
    return this.renderPage(
      tag,
      html`${page}<iris-command-palette></iris-command-palette><iris-shortcuts-dialog></iris-shortcuts-dialog>`,
    );
  }

  // WebSocket: live notifications.
  // The backend broadcasts `notification_new` on the user's room for every
  // mention/assignment/comment. We listen here (app-level, persistent) so the
  // unread badge + inbox update live regardless of which page is open.
  #attachWs(ws: WebSocket): void {
    this.#detachWs();
    this.#wsPrev = ws;
    this.#wsMessageHandler = (e: MessageEvent) => {
      let data: { type?: string; payload?: DtoNotificationResponse };
      try {
        data = JSON.parse(e.data);
      } catch {
        return;
      }
      if (data.type === "notification_new" && data.payload) {
        handleNotificationEvent(data.payload as DtoNotificationResponse);
        this.#maybeShowDesktopNotification(
          data.payload as DtoNotificationResponse,
        );
      }
      // Keeps the approvals badge current as requests are created, decided
      // or cancelled anywhere in the workspace.
      if (data.type === "deployment_updated") {
        fetchApprovalSummary();
      }
    };
    ws.addEventListener("message", this.#wsMessageHandler);
  }

  #detachWs(): void {
    if (this.#wsPrev && this.#wsMessageHandler) {
      this.#wsPrev.removeEventListener("message", this.#wsMessageHandler);
    }
    this.#wsPrev = null;
    this.#wsMessageHandler = null;
  }

  /**
   * Fire an OS-level desktop notification for an incoming WS event when the
   * user has `desktop_notifications` enabled AND the tab is hidden (so we
   * don't spam a notification for something the user is already looking at).
   * Permission is requested lazily on the first eligible event.
   */
  #maybeShowDesktopNotification(
    n: DtoNotificationResponse,
  ): void {
    if (!desktopNotificationsEnabled()) return;
    if (typeof Notification === "undefined") return;
    // Only notify when the app isn't focused: avoids duplicate alerts for
    // the in-app toast + badge the user already sees.
    if (!document.hidden) return;
    const fire = (): void => {
      try {
        const notif = new Notification(n.title ?? "IRIS", {
          body: n.body ?? "",
          tag: n.id ?? undefined,
        });
        notif.onclick = () => {
          window.focus();
          if (n.link) navigate(n.link);
          notif.close();
        };
      } catch {
        // Some browsers throw if the service worker registration is missing;
        // silently ignore: the in-app notification still landed.
      }
    };
    if (Notification.permission === "granted") {
      fire();
    } else if (Notification.permission === "default") {
      Notification.requestPermission().then((perm) => {
        if (perm === "granted") fire();
      });
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#detachWs();
  }

  protected render() {
    const path = currentPath.value;
    const { isLoading, isAuthenticated } = auth.value;

    if (isLoading) {
      return this.renderPage(
        "__loader__",
        html`
          <div class="app-loader">
            <iris-spinner></iris-spinner>
          </div>
        `,
        false,
      );
    }

    if (!isAuthenticated) {
      // The emailed sign-in link lands here; it must be reachable without a
      // session, since redeeming it is what creates one.
      if (path === "/auth/callback") {
        return this.renderPage(
          "iris-magic-link-callback-page",
          html`<iris-magic-link-callback-page></iris-magic-link-callback-page>`,
          false,
        );
      }
      return this.renderPage(
        "__login__",
        html`<iris-login-page></iris-login-page>`,
        false,
      );
    }

    if (path === "/apps") {
      return this.pageWithPalette(
        "iris-apps-page",
        html`<iris-apps-page></iris-apps-page>`,
      );
    }

    if (matchRoute("/apps/:slug", path)) {
      return this.pageWithPalette(
        "iris-app-detail-page",
        html`<iris-app-detail-page></iris-app-detail-page>`,
      );
    }

    if (matchRoute("/apps/:slug/releases/:releaseId", path)) {
      return this.pageWithPalette(
        "iris-release-detail-page",
        html`<iris-release-detail-page></iris-release-detail-page>`,
      );
    }

    if (matchRoute("/apps/:slug/deployments/:deploymentId", path)) {
      return this.pageWithPalette(
        "iris-deployment-detail-page",
        html`<iris-deployment-detail-page></iris-deployment-detail-page>`,
      );
    }

    if (matchRoute("/release-management/:tab", path)) {
      return this.pageWithPalette(
        "iris-release-management-page",
        html`<iris-release-management-page></iris-release-management-page>`,
      );
    }

    if (matchRoute("/sprint-dashboard/:tab", path)) {
      return this.pageWithPalette(
        "iris-sprint-dashboard-page",
        html`<iris-sprint-dashboard-page></iris-sprint-dashboard-page>`,
      );
    }

    if (path === "/projects") {
      return this.pageWithPalette(
        "iris-projects-page",
        html`<iris-projects-page></iris-projects-page>`,
      );
    }

    if (matchRoute("/projects/:slug", path)) {
      return this.pageWithPalette(
        "iris-project-detail-page",
        html`<iris-project-detail-page></iris-project-detail-page>`,
      );
    }

    if (path === "/inbox") {
      return this.pageWithPalette(
        "iris-inbox-page",
        html`<iris-inbox-page></iris-inbox-page>`,
      );
    }

    if (path === "/members" || matchRoute("/members/:tab", path)) {
      return this.pageWithPalette(
        "iris-members-page",
        html`<iris-members-page></iris-members-page>`,
      );
    }

    if (path === "/my-tasks") {
      return this.pageWithPalette(
        "iris-my-tasks-page",
        html`<iris-my-tasks-page></iris-my-tasks-page>`,
      );
    }

    if (path === "/preferences") {
      return this.renderPage(
        "iris-user-settings-page",
        html`<iris-user-settings-page></iris-user-settings-page>`,
      );
    }

    if (
      path === "/settings/workspace" ||
      matchRoute("/settings/workspace/:tab", path)
    ) {
      return this.pageWithPalette(
        "iris-workspace-settings-page",
        html`<iris-workspace-settings-page></iris-workspace-settings-page>`,
      );
    }

    // Guest-only routes and the bare Sprint Dashboard URL can briefly remain
    // active while their compatibility redirect settles.
    if (
      path === "/" ||
      path === "/login" ||
      path === "/auth/callback" ||
      path === "/sprint-dashboard"
    ) {
      return this.pageWithPalette(
        "iris-sprint-dashboard-page",
        html`<iris-sprint-dashboard-page></iris-sprint-dashboard-page>`,
      );
    }

    return this.pageWithPalette(
      "iris-not-found-page",
      html`<iris-not-found-page></iris-not-found-page>`,
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "iris-app": IrisApp;
  }
}
